/**
 * Image Sharing.
 * Doc: docs/overall-plan/02-MANGO-PERSONAL/image-sharing.
 * Backend: image-albums.routes.ts (personal-scoped). Personal photo albums shared with connections.
 */
import React, { useEffect, useState } from 'react';
import ComingSoonPage from '../components/ComingSoonPage';
import { imageAlbumsApi, type PersonalAlbumItem, type PersonalPhotoItem } from '../services/api';

const ImageSharing: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [albums, setAlbums] = useState<PersonalAlbumItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingAlbumId, setDeletingAlbumId] = useState<string | null>(null);

  const [openAlbum, setOpenAlbum] = useState<PersonalAlbumItem | null>(null);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [albumError, setAlbumError] = useState<string | null>(null);

  const [photoUrl, setPhotoUrl] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [busyPhotoId, setBusyPhotoId] = useState<string | null>(null);

  const [shareConnectionId, setShareConnectionId] = useState('');
  const [sharing, setSharing] = useState(false);

  const loadData = React.useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res: any = await imageAlbumsApi.list();
      if (res?.success && Array.isArray(res?.data)) {
        setAlbums(res.data);
      } else if (res?.error) {
        setError(res.error);
      }
    } catch {
      setError('Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddAlbum = async () => {
    if (!newTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res: any = await imageAlbumsApi.create({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
      });
      if (res?.success) {
        setNewTitle('');
        setNewDescription('');
        setShowAdd(false);
        loadData(true);
      } else setError(res?.error || 'Failed to create album');
    } catch {
      setError('Failed to create album');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAlbum = async (id: string) => {
    setDeletingAlbumId(id);
    setError(null);
    try {
      const res: any = await imageAlbumsApi.deleteAlbum(id);
      if (res?.success) {
        if (openAlbum?.id === id) setOpenAlbum(null);
        loadData(true);
      } else setError(res?.error || 'Failed to delete album');
    } catch {
      setError('Failed to delete album');
    } finally {
      setDeletingAlbumId(null);
    }
  };

  const handleOpenAlbum = async (album: PersonalAlbumItem) => {
    setOpenAlbum(album);
    setAlbumError(null);
    setAlbumLoading(true);
    try {
      const res: any = await imageAlbumsApi.get(album.id);
      if (res?.success && res?.data) {
        setOpenAlbum(res.data);
      } else {
        setAlbumError(res?.error || 'Failed to load album');
      }
    } catch {
      setAlbumError('Failed to load album');
    } finally {
      setAlbumLoading(false);
    }
  };

  const refreshOpenAlbum = async () => {
    if (!openAlbum) return;
    try {
      const res: any = await imageAlbumsApi.get(openAlbum.id);
      if (res?.success && res?.data) setOpenAlbum(res.data);
    } catch {
      // keep existing state on transient failure
    }
  };

  const handleAddPhoto = async () => {
    if (!openAlbum || !photoUrl.trim()) return;
    setAddingPhoto(true);
    setAlbumError(null);
    try {
      const res: any = await imageAlbumsApi.addPhoto(openAlbum.id, {
        mediaUrl: photoUrl.trim(),
        caption: photoCaption.trim() || undefined,
      });
      if (res?.success) {
        setPhotoUrl('');
        setPhotoCaption('');
        refreshOpenAlbum();
        loadData(true);
      } else setAlbumError(res?.error || 'Failed to add photo');
    } catch {
      setAlbumError('Failed to add photo');
    } finally {
      setAddingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    setBusyPhotoId(photoId);
    setAlbumError(null);
    try {
      const res: any = await imageAlbumsApi.deletePhoto(photoId);
      if (res?.success) {
        refreshOpenAlbum();
        loadData(true);
      } else setAlbumError(res?.error || 'Failed to delete photo');
    } catch {
      setAlbumError('Failed to delete photo');
    } finally {
      setBusyPhotoId(null);
    }
  };

  const handleShare = async () => {
    if (!openAlbum || !shareConnectionId.trim()) return;
    setSharing(true);
    setAlbumError(null);
    try {
      const res: any = await imageAlbumsApi.share(openAlbum.id, shareConnectionId.trim());
      if (res?.success) {
        setShareConnectionId('');
        refreshOpenAlbum();
      } else setAlbumError(res?.error || 'Failed to share album');
    } catch {
      setAlbumError('Failed to share album');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px', textAlign: 'center', color: '#666' }}>
        Loading…
      </div>
    );
  }

  if (error && albums.length === 0) {
    return (
      <ComingSoonPage
        title="Image Sharing"
        icon="📷"
        subtitle="Personal photo albums shared with your connections"
        description={error}
        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      />
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>📷 Image Sharing</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Personal photo albums shared with your connections
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setShowAdd((s) => !s)}
              style={{
                padding: '8px 16px',
                background: 'rgba(76,175,80,0.9)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Add Album
            </button>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '8px',
                color: 'white',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #e2e8f0',
        }}>
          <input
            type="text"
            placeholder="Album title (e.g. Summer Trip)"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            placeholder="Description (optional)"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: '10px',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              fontSize: '14px',
            }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleAddAlbum}
              disabled={submitting || !newTitle.trim()}
              style={{
                padding: '8px 16px',
                background: '#16a34a',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {submitting ? 'Adding…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewTitle(''); setNewDescription(''); }}
              style={{
                padding: '8px 16px',
                background: '#64748b',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p style={{ color: '#b91c1c', marginBottom: '16px' }}>{error}</p>}

      {albums.length === 0 && !error && !showAdd && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          <p style={{ margin: '0 0 12px 0' }}>No albums yet. Create your first personal photo album.</p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            style={{
              padding: '8px 16px',
              background: '#667eea',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Create your first album
          </button>
        </div>
      )}

      {albums.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: '16px',
        }}>
          {albums.map((album) => (
            <button
              key={album.id}
              type="button"
              onClick={() => handleOpenAlbum(album)}
              style={{
                display: 'block',
                textAlign: 'left',
                borderRadius: '12px',
                overflow: 'hidden',
                background: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                padding: 0,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <div style={{ aspectRatio: '1', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {album.coverImageUrl ? (
                  <img src={album.coverImageUrl} alt={album.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '32px', color: '#ccc' }}>📷</span>
                )}
              </div>
              <div style={{ padding: '10px 12px' }}>
                <strong style={{ fontSize: '14px', color: '#333' }}>{album.title}</strong>
                {album.description && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>{album.description}</p>
                )}
                {album.sharedWithConnectionIds && album.sharedWithConnectionIds.length > 0 && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#667eea' }}>
                    Shared with {album.sharedWithConnectionIds.length}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {openAlbum && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={() => setOpenAlbum(null)}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '640px', width: '100%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px' }}>{openAlbum.title}</h2>
                {openAlbum.description && <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>{openAlbum.description}</p>}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => handleDeleteAlbum(openAlbum.id)}
                  disabled={deletingAlbumId === openAlbum.id}
                  style={{ padding: '6px 12px', background: '#dc2626', border: 'none', borderRadius: '6px', color: 'white', cursor: deletingAlbumId === openAlbum.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                >
                  {deletingAlbumId === openAlbum.id ? '…' : 'Delete Album'}
                </button>
                <button type="button" onClick={() => setOpenAlbum(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#666' }}>×</button>
              </div>
            </div>

            {albumError && <p style={{ color: '#b91c1c', fontSize: '13px', marginBottom: '12px' }}>{albumError}</p>}

            {albumLoading ? (
              <p style={{ color: '#666' }}>Loading album…</p>
            ) : (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '12px',
                  marginBottom: '16px',
                }}>
                  {(openAlbum.photos || []).map((photo: PersonalPhotoItem) => (
                    <div key={photo.id} style={{ borderRadius: '8px', overflow: 'hidden', background: '#f0f0f0' }}>
                      <img src={photo.mediaUrl} alt={photo.caption || 'Photo'} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
                      {photo.caption && <p style={{ margin: '4px 6px', fontSize: '11px', color: '#666' }}>{photo.caption}</p>}
                      <button
                        type="button"
                        onClick={() => handleDeletePhoto(photo.id)}
                        disabled={busyPhotoId === photo.id}
                        style={{ display: 'block', width: '100%', padding: '4px', background: '#dc2626', border: 'none', color: 'white', cursor: busyPhotoId === photo.id ? 'not-allowed' : 'pointer', fontSize: '11px' }}
                      >
                        {busyPhotoId === photo.id ? '…' : 'Delete'}
                      </button>
                    </div>
                  ))}
                  {(!openAlbum.photos || openAlbum.photos.length === 0) && (
                    <p style={{ fontSize: '13px', color: '#888', gridColumn: '1 / -1' }}>No photos yet.</p>
                  )}
                </div>

                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ fontSize: '13px', color: '#333' }}>Add photo</strong>
                  <input
                    type="text"
                    placeholder="Photo URL"
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', margin: '8px 0', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                  />
                  <input
                    type="text"
                    placeholder="Caption (optional)"
                    value={photoCaption}
                    onChange={(e) => setPhotoCaption(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', marginBottom: '8px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                  />
                  <button
                    type="button"
                    onClick={handleAddPhoto}
                    disabled={addingPhoto || !photoUrl.trim()}
                    style={{ padding: '8px 14px', background: '#16a34a', border: 'none', borderRadius: '8px', color: 'white', cursor: addingPhoto ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                  >
                    {addingPhoto ? 'Adding…' : 'Add Photo'}
                  </button>
                </div>

                <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ fontSize: '13px', color: '#333' }}>Share with a connection</strong>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <input
                      type="text"
                      placeholder="Connection ID"
                      value={shareConnectionId}
                      onChange={(e) => setShareConnectionId(e.target.value)}
                      style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px' }}
                    />
                    <button
                      type="button"
                      onClick={handleShare}
                      disabled={sharing || !shareConnectionId.trim()}
                      style={{ padding: '8px 14px', background: '#667eea', border: 'none', borderRadius: '8px', color: 'white', cursor: sharing ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                    >
                      {sharing ? 'Sharing…' : 'Share'}
                    </button>
                  </div>
                  {openAlbum.sharedWithConnectionIds && openAlbum.sharedWithConnectionIds.length > 0 && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                      Shared with: {openAlbum.sharedWithConnectionIds.join(', ')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageSharing;
